import {IconAdjustmentsHorizontal, IconArrowRight, IconChevronDown, IconSearch, IconX} from "@tabler/icons-react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {getProviderLogo} from "@/components/Stories/stories.constants";
import {useMiniGames} from "@/store/useMiniGames";
import css from "./GameSearchBar.module.css";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const ProviderFilterContent = ({normalizedProviders, activeProvider, onProviderClick, providersLabel}) => (
  <div className={cx(css.filterGroup, css.providerGroup)}>
    <span className={css.filterLabel}>{providersLabel}</span>
    <div className={css.providerGrid}>
      {normalizedProviders.map(opt => {
        const avatar = getProviderLogo(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            className={cx(css.providerCard, activeProvider === opt.value && css.providerCardActive)}
            onClick={() => onProviderClick(opt.value)}
          >
            {avatar ? (
              <img
                src={avatar}
                alt={opt.label}
                className={css.providerCardAvatar}
              />
            ) : (
              <span className={css.providerCardFallback}>{opt.label.charAt(0).toUpperCase()}</span>
            )}
            <span className={css.providerCardName}>{opt.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const CategoryFilterContent = ({normalizedCategories, activeCategory, onCategoryClick, categoriesLabel}) => (
  <div className={cx(css.filterGroup, css.categoryGroup)}>
    <span className={css.filterLabel}>{categoriesLabel}</span>
    <div className={css.chipsRow}>
      {normalizedCategories.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={cx(css.chip, activeCategory === opt.value && css.chipActive)}
          onClick={() => onCategoryClick(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const SearchInputField = ({
  inputRef,
  searchValue,
  placeholder,
  searchPlaceholder,
  searchAriaLabel,
  searchClearLabel,
  searchSubmitLabel,
  isFocused,
  onFocus,
  onBlur,
  onChange,
  onKeyDown,
  onClear,
  onSubmit,
}) => (
  <div className={cx(css.inputWrapper, isFocused && css.inputWrapperFocused)}>
    <IconSearch
      size={18}
      stroke={2}
      className={css.searchIcon}
    />
    <input
      ref={inputRef}
      type="search"
      className={css.input}
      placeholder={placeholder || searchPlaceholder}
      value={searchValue}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      aria-label={searchAriaLabel}
    />
    {searchValue && (
      <button
        type="button"
        className={css.clearBtn}
        onClick={onClear}
        aria-label={searchClearLabel}
      >
        <IconX
          size={14}
          stroke={2.5}
        />
      </button>
    )}
    {isFocused && (
      <button
        type="button"
        className={cx(css.goBtn, searchValue.trim() && css.goBtnActive)}
        onMouseDown={e => {
          e.preventDefault();
          onSubmit();
        }}
        aria-label={searchSubmitLabel}
      >
        <IconArrowRight
          size={18}
          stroke={2.5}
        />
      </button>
    )}
  </div>
);

const GameSearchBar = ({
  onSearch,
  onSubmit,
  initialValue = "",
  providerOptions = [],
  categoryOptions = [],
  activeProvider = "",
  activeCategory = "",
  onProviderChange,
  onCategoryChange,
  resultCount,
  placeholder,
  hideProviderFilter = false,
  filterLayout = "split",
}) => {
  const {t} = useTranslation();
  const providerDisplayNames = useMiniGames(state => state.providerDisplayNames);
  const isSplitLayout = filterLayout === "split";
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(null);
  const [searchExpanded, setSearchExpanded] = useState(() => isSplitLayout && Boolean(initialValue?.trim()));
  const [searchValue, setSearchValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const providersLabel = t("gameSearchProviders", {defaultValue: "Proveedores"});
  const categoriesLabel = t("gameSearchCategories", {defaultValue: "Categorías"});
  const searchPlaceholder = t("gameSearchPlaceholder", {defaultValue: "Buscar juegos..."});
  const searchAriaLabel = t("gameSearchAriaLabel", {defaultValue: "Buscar juegos"});
  const searchClearLabel = t("gameSearchClear", {defaultValue: "Limpiar búsqueda"});
  const searchSubmitLabel = t("gameSearchSubmit", {defaultValue: "Buscar"});

  useEffect(() => {
    setSearchValue(prev => (prev === initialValue ? prev : initialValue));
  }, [initialValue]);

  useEffect(() => {
    if (!isSplitLayout) return;
    if (initialValue?.trim()) {
      setSearchExpanded(true);
    }
  }, [initialValue, isSplitLayout]);

  const hasActiveFilters = activeProvider || activeCategory;
  const isAnyFilterOpen = isSplitLayout ? Boolean(openFilter) : filtersOpen;

  const closeFilters = useCallback(() => {
    if (isSplitLayout) {
      setOpenFilter(null);
      return;
    }
    setFiltersOpen(false);
  }, [isSplitLayout]);

  const handleSearch = useCallback(
    e => {
      const value = e.target.value;
      setSearchValue(value);
      onSearch?.(value);
    },
    [onSearch]
  );

  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter" && searchValue.trim()) {
        onSubmit?.(searchValue.trim());
      }
    },
    [onSubmit, searchValue]
  );

  const handleSubmit = useCallback(() => {
    if (searchValue.trim()) {
      onSubmit?.(searchValue.trim());
      onSearch?.(searchValue.trim());
    }
  }, [onSubmit, onSearch, searchValue]);

  const handleClear = useCallback(() => {
    setSearchValue("");
    onSearch?.("");
    inputRef.current?.focus();
  }, [onSearch]);

  const handleProviderClick = useCallback(
    provider => {
      onProviderChange?.(provider === activeProvider ? "" : provider);
      closeFilters();
    },
    [onProviderChange, activeProvider, closeFilters]
  );

  const handleCategoryClick = useCallback(
    category => {
      onCategoryChange?.(category === activeCategory ? "" : category);
      closeFilters();
    },
    [onCategoryChange, activeCategory, closeFilters]
  );

  const handleClearAll = useCallback(() => {
    onProviderChange?.("");
    onCategoryChange?.("");
    closeFilters();
  }, [onProviderChange, onCategoryChange, closeFilters]);

  const toggleSplitFilter = useCallback(filterKey => {
    setSearchExpanded(false);
    setOpenFilter(prev => (prev === filterKey ? null : filterKey));
  }, []);

  const toggleSearchExpanded = useCallback(() => {
    setOpenFilter(null);
    setSearchExpanded(prev => {
      const next = !prev;
      if (next) {
        requestAnimationFrame(() => inputRef.current?.focus());
      }
      return next;
    });
  }, []);

  const searchInputProps = {
    inputRef,
    searchValue,
    placeholder,
    searchPlaceholder,
    searchAriaLabel,
    searchClearLabel,
    searchSubmitLabel,
    isFocused,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onChange: handleSearch,
    onKeyDown: handleKeyDown,
    onClear: handleClear,
    onSubmit: handleSubmit,
  };

  const normalizedProviders = useMemo(
    () => providerOptions.map(opt => (typeof opt === "string" ? {value: opt, label: opt} : opt)),
    [providerOptions]
  );

  const normalizedCategories = useMemo(
    () => categoryOptions.map(opt => (typeof opt === "string" ? {value: opt, label: opt} : opt)),
    [categoryOptions]
  );

  const activeProviderLabel = useMemo(() => {
    if (!activeProvider) return "";
    return (
      providerDisplayNames[activeProvider] ??
      normalizedProviders.find(opt => opt.value === activeProvider)?.label ??
      activeProvider
    );
  }, [providerDisplayNames, activeProvider, normalizedProviders]);

  const activeCategoryLabel = useMemo(() => {
    if (!activeCategory) return "";
    return normalizedCategories.find(opt => opt.value === activeCategory)?.label ?? activeCategory;
  }, [activeCategory, normalizedCategories]);

  const hasProviderOptions = normalizedProviders.length > 0;
  const hasCategoryOptions = normalizedCategories.length > 0;
  const showProviderFilter = isSplitLayout
    ? !hideProviderFilter
    : !hideProviderFilter && hasProviderOptions;
  const showCategoryFilter = isSplitLayout ? true : hasCategoryOptions;
  const showFilters = showProviderFilter || showCategoryFilter;
  const hasActiveSearch = Boolean(searchValue.trim());

  return (
    <div className={css.toolbar}>
      {isSplitLayout ? (
        <>
          <div className={css.toolbarRow}>
            <button
              type="button"
              className={cx(
                css.searchTrigger,
                searchExpanded && css.searchTriggerOpen,
                hasActiveSearch && css.searchTriggerHasValue
              )}
              onClick={toggleSearchExpanded}
              aria-label={searchAriaLabel}
              aria-expanded={searchExpanded}
            >
              <IconSearch
                size={20}
                stroke={2}
              />
              <span className={css.searchTriggerText}>{searchSubmitLabel}</span>
              {hasActiveSearch && <span className={css.searchTriggerBadge} />}
            </button>

            {showProviderFilter && (
              <button
                type="button"
                className={cx(
                  css.filterTrigger,
                  css.filterTriggerProvider,
                  openFilter === "provider" && css.filterTriggerOpen,
                  activeProvider && css.filterTriggerHasValue
                )}
                onClick={() => hasProviderOptions && toggleSplitFilter("provider")}
                disabled={!hasProviderOptions}
                aria-expanded={openFilter === "provider"}
                aria-label={providersLabel}
              >
                <span className={css.filterTriggerText}>
                  {activeProvider ? activeProviderLabel : providersLabel.toUpperCase()}
                </span>
                <IconChevronDown
                  size={16}
                  stroke={2}
                  className={cx(css.filterTriggerChevron, openFilter === "provider" && css.filterTriggerChevronOpen)}
                />
              </button>
            )}

            {showCategoryFilter && (
              <button
                type="button"
                className={cx(
                  css.filterTrigger,
                  css.filterTriggerCategory,
                  openFilter === "category" && css.filterTriggerOpen,
                  activeCategory && css.filterTriggerHasValue
                )}
                onClick={() => hasCategoryOptions && toggleSplitFilter("category")}
                disabled={!hasCategoryOptions}
                aria-expanded={openFilter === "category"}
                aria-label={categoriesLabel}
              >
                <span className={css.filterTriggerText}>{activeCategory ? activeCategoryLabel : categoriesLabel}</span>
                <IconChevronDown
                  size={16}
                  stroke={2}
                  className={cx(css.filterTriggerChevron, openFilter === "category" && css.filterTriggerChevronOpen)}
                />
              </button>
            )}
          </div>

          {searchExpanded && (
            <div className={css.searchExpandedRow}>
              <SearchInputField {...searchInputProps} />
            </div>
          )}
        </>
      ) : (
        <div className={css.searchRow}>
          <SearchInputField {...searchInputProps} />

          {showFilters && (
            <button
              type="button"
              className={cx(css.filterToggle, filtersOpen && css.filterToggleActive)}
              onClick={() => setFiltersOpen(prev => !prev)}
              aria-label={t("gameSearchFilters", {defaultValue: "Filtros"})}
              aria-expanded={filtersOpen}
            >
              <IconAdjustmentsHorizontal
                size={20}
                stroke={2}
              />
              {hasActiveFilters && <span className={css.filterBadge} />}
            </button>
          )}
        </div>
      )}

      {isSplitLayout && openFilter === "provider" && hasProviderOptions && (
        <div className={css.filtersPanel}>
          <ProviderFilterContent
            normalizedProviders={normalizedProviders}
            activeProvider={activeProvider}
            onProviderClick={handleProviderClick}
            providersLabel={providersLabel}
          />
        </div>
      )}

      {isSplitLayout && openFilter === "category" && hasCategoryOptions && (
        <div className={css.filtersPanel}>
          <CategoryFilterContent
            normalizedCategories={normalizedCategories}
            activeCategory={activeCategory}
            onCategoryClick={handleCategoryClick}
            categoriesLabel={categoriesLabel}
          />
        </div>
      )}

      {/* Active filter tags (panel layout only) */}
      {!isSplitLayout && hasActiveFilters && !isAnyFilterOpen && (
        <div className={css.activeFilters}>
          {activeProvider && (
            <span className={css.activeTag}>
              {activeProviderLabel}
              <button
                type="button"
                className={css.removeTag}
                onClick={() => onProviderChange?.("")}
                aria-label={t("gameSearchRemoveFilter", {defaultValue: "Quitar filtro"})}
              >
                <IconX
                  size={12}
                  stroke={2.5}
                />
              </button>
            </span>
          )}
          {activeCategory && (
            <span className={cx(css.activeTag, css.activeTagCategory)}>
              {activeCategoryLabel}
              <button
                type="button"
                className={css.removeTag}
                onClick={() => onCategoryChange?.("")}
                aria-label={t("gameSearchRemoveFilter", {defaultValue: "Quitar filtro"})}
              >
                <IconX
                  size={12}
                  stroke={2.5}
                />
              </button>
            </span>
          )}
          <button
            type="button"
            className={css.clearAll}
            onClick={handleClearAll}
          >
            {t("gameSearchClearFilters", {defaultValue: "Limpiar filtros"})}
          </button>
        </div>
      )}

      {isSplitLayout && hasActiveFilters && (
        <div className={css.activeFilters}>
          <button
            type="button"
            className={css.clearAll}
            onClick={handleClearAll}
          >
            {t("gameSearchClearFilters", {defaultValue: "Limpiar filtros"})}
          </button>
        </div>
      )}

      {/* Combined filter panel (panel layout only) */}
      {!isSplitLayout && filtersOpen && showFilters && (
        <div className={css.filtersPanel}>
          {showProviderFilter && (
            <ProviderFilterContent
              normalizedProviders={normalizedProviders}
              activeProvider={activeProvider}
              onProviderClick={handleProviderClick}
              providersLabel={providersLabel}
            />
          )}

          {showCategoryFilter && (
            <CategoryFilterContent
              normalizedCategories={normalizedCategories}
              activeCategory={activeCategory}
              onCategoryClick={handleCategoryClick}
              categoriesLabel={categoriesLabel}
            />
          )}
        </div>
      )}

      {/* Result count */}
      {resultCount !== undefined && searchValue && (
        <span className={css.resultCount}>
          {resultCount} {t("gameSearchResults", {defaultValue: "resultados"})}
        </span>
      )}
    </div>
  );
};

export default GameSearchBar;
